<script setup lang="ts">
const { y } = useWindowScroll();

const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});

const showBackToTop = computed(() => isMounted.value && y.value > 200);

// Calculate scroll percentage
const scrollPercentage = computed(() => {
  if (!isMounted.value) return 0;
  
  const scrollTop = y.value;
  const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
  const percentage = Math.min((scrollTop / documentHeight) * 100, 100);
  
  return Math.round(percentage);
});

// Calculate SVG circle parameters
const radius = 18;
const circumference = 2 * Math.PI * radius;
const strokeDasharray = computed(() => circumference);
const strokeDashoffset = computed(() => {
  const percentage = scrollPercentage.value;
  return circumference - (percentage / 100) * circumference;
});

const scrollToTop = (): void => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>

<template>
  <div class="fixed z-[999] bottom-6 right-6 lg:flex hidden transition-all duration-300 ease-in-out" :class="{ 'opacity-0 invisible scale-0': !showBackToTop }">
    <!-- Progress circle wrapper -->
    <div class="relative w-16 h-16">
      <!-- SVG Progress Circle -->
      <svg 
        class="absolute inset-0 w-full h-full -rotate-90 opacity-40 pointer-events-none" 
        viewBox="0 0 48 48"
      >
        <!-- Background circle -->
        <circle
          cx="24"
          cy="24"
          :r="radius"
          fill="none"
          stroke="#e5e7eb"
          stroke-width="2"
        />
        <!-- Progress circle -->
        <circle
          cx="24"
          cy="24"
          :r="radius"
          fill="none"
          stroke="#231F20"
          stroke-width="2"
          stroke-linecap="round"
          :stroke-dasharray="strokeDasharray"
          :stroke-dashoffset="strokeDashoffset"
          class="transition-all duration-300 ease-out"
        />
      </svg>
      
      <!-- Inner button -->
      <button
        id="backToTop"
        class="m-[7px] absolute z-[999] inset-1 bg-primary text-white rounded-full flex items-center justify-center transition-all duration-300 hover:bg-primary/90 shadow-lg"
        :aria-label="$t('footer.backToTopAriaLabel')"
        @click="scrollToTop">
        <i class="icon-arrow-right rotate-[270deg] text-base"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Ensure smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style>